import * as React from "react";
import styled, { css } from "styled-components";
import { Form, Formik } from "formik";
import { getValueFromInput } from "util/FormsUtils";
import { ConfigurationsActions } from "stores/configurations/ConfigurationsStore";
import { ConfigurationType } from "components/configurations/ConfigurationTypes";
import { Alert, Table, Modal } from "components/bootstrap";
import { FormikInput, ModalSubmit, SortableList } from "components/common";
import * as ISODurationUtils from "util/ISODurationUtils";
import MessageProcessorStatusFormGroup from "components/configurations/message-processors/MessageProcessorStatusFormGroup";
import useProductName from "brand-customization/useProductName";
const LabelSpan = styled.span(
  ({ theme }) => css`
    margin-left: ${theme.spacings.sm};
    font-weight: bold;
  `
);
const ProcessingConfigModalForm = ({ closeModal, formConfig }) => {
  const productName = useProductName();
  const futureTimestampNormalizationHelpText = `Enable normalisation of timestamps that specify a time significantly ahead of the ${productName} system time. This typically happens when a log source runs on a server with an incorrect system clock. Future timestamps will be normalised to match the time it was first received by either a ${productName} Forwarder, or ${productName}. It is important to prevent future timestamps when making use of the Warm Tier, as this can otherwise degrade performance.`;
  const hasNoActiveProcessor = () => formConfig.disabled_processors.length >= formConfig.processor_order.length;
  const saveConfig = (values) => {
    if (!hasNoActiveProcessor()) {
      const { processor_order, disabled_processors, grace_period } = values;
      Promise.allSettled([
        ConfigurationsActions.updateMessageProcessorsConfig(ConfigurationType.MESSAGE_PROCESSORS_CONFIG, {
          processor_order,
          disabled_processors
        }),
        ConfigurationsActions.update(ConfigurationType.GLOBAL_PROCESSING_RULE_CONFIG, { grace_period })
      ]).then(() => {
        closeModal();
      });
    }
  };
  const updateSorting = (newSorting, setFieldValue) => {
    const processorOrder = newSorting.map((item) => ({ class_name: item.id, name: item.title }));
    setFieldValue("processor_order", processorOrder);
  };
  const sortableItems = (formValues) => formValues.processor_order.map((processor) => ({ id: processor.class_name, title: processor.name }));
  const handleEnableFutureTimestampNormalisation = (enabled, setFieldValue) => {
    if (enabled) {
      setFieldValue("grace_period", (formConfig == null ? void 0 : formConfig.grace_period) || "P2D");
    } else {
      setFieldValue("grace_period", void 0);
    }
  };
  const gracePeriodValidator = (milliseconds) => milliseconds >= 60 * 1e3;
  const validateGracePeriodField = (enabled) => (value) => {
    let errorMessage = "";
    if (enabled && !ISODurationUtils.isValidDuration(value, gracePeriodValidator)) {
      errorMessage = "Grace Period is invalid";
    }
    return errorMessage;
  };
  return /* @__PURE__ */ React.createElement(Modal, { show: true, "data-testid": "config-modal", onHide: closeModal }, /* @__PURE__ */ React.createElement(Formik, { onSubmit: saveConfig, initialValues: formConfig }, ({ isSubmitting, values, setFieldValue, isValid }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, "Update Message Processors Configuration")), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h2", null, "Global Processing Rules Configuration"), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "enableFutureTimestampNormalization",
      id: "enableFutureTimestampNormalization",
      help: futureTimestampNormalizationHelpText,
      onChange: (event) => handleEnableFutureTimestampNormalisation(Boolean(getValueFromInput(event.target)), setFieldValue),
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Future Timestamp Normalization")
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      name: "grace_period",
      id: "grace_period",
      placeholder: "P2D",
      label: "Grace Period",
      disabled: !(values == null ? void 0 : values.enableFutureTimestampNormalization),
      help: `If Future Timestamp Normalisation is enabled, timestamps specifying a time further ahead of the ${productName} system time than the Grace Period interval will be normalised.`,
      addonAfter: values.enableFutureTimestampNormalization && ISODurationUtils.formatDuration(values.grace_period, gracePeriodValidator, "invalid"),
      validate: validateGracePeriodField(values.enableFutureTimestampNormalization),
      required: true
    }
  ), /* @__PURE__ */ React.createElement("h2", null, "Message Processors Configuration"), /* @__PURE__ */ React.createElement("h3", null, "Order"), /* @__PURE__ */ React.createElement("p", null, "Use drag and drop to change the execution order of the message processors."), /* @__PURE__ */ React.createElement(
    SortableList,
    {
      items: sortableItems(values),
      onMoveItem: (newSorting) => updateSorting(newSorting, setFieldValue),
      displayOverlayInPortal: true
    }
  ), /* @__PURE__ */ React.createElement("h3", null, "Status"), /* @__PURE__ */ React.createElement("p", null, "Change the checkboxes to change the status of a message processor."), /* @__PURE__ */ React.createElement(Table, { bordered: true, condensed: true, className: "top-margin" }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, "Processor"), /* @__PURE__ */ React.createElement("th", null, "Enabled"))), /* @__PURE__ */ React.createElement("tbody", null, /* @__PURE__ */ React.createElement(MessageProcessorStatusFormGroup, null))), hasNoActiveProcessor() && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", title: "Error" }, "No active message processor!"))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      onCancel: closeModal,
      isSubmitting,
      disabledSubmit: !isValid,
      isAsyncSubmit: true,
      submitLoadingText: "Update configuration",
      submitButtonText: "Update configuration"
    }
  )))));
};
export default ProcessingConfigModalForm;
